import React, { useCallback, useEffect, useState, useMemo } from 'react';
import useLayer from 'app/hook/common/useLayer';
import LayerComm from 'app/components/common/LayerComm';
import PaymentCharge from 'app/components/my/affiliate/PaymentCharge';
import PaymentServiceDetail from 'app/components/my/affiliate/PaymentServiceDetail';
import PaymentAdjustmentDetail from 'app/components/my/affiliate/PaymentAdjustmentDetail';
import PaymentDealerDetail from 'app/components/my/affiliate/PaymentDealerDetail';
import PaymentChargeLayer from 'app/components/my/affiliate/PaymentChargeLayer';
import PaymentChargeCompleteLayer from 'app/components/my/affiliate/PaymentChargeCompleteLayer';
import PaymentHistoryMonthLayer from 'app/components/my/affiliate/PaymentHistoryMonthLayer';
import MaintenanceNotice from 'app/components/my/affiliate/MaintenanceNotice/MaintenanceNotice';
import useMaintenanceCheck from 'app/hook/my/affiliate/useMaintenanceCheck';
import dayjs from 'app/libs/dayjs';
import useNow from 'app/hook/common/useNow';
import * as api from '@encarpkg/api';
import { loggingException } from 'app/libs/sentry';
import useAuth from 'app/hook/common/useAuth';
import { clearAuthInfo } from 'app/hook/login/useLogout';
import { useHistory, useLocation } from 'react-router-dom';
import qs from 'query-string';
import padStart from 'lodash/padStart';
import LoadingBar from 'app/components/common/LoadingBar';
import { cutting10units, numberWithCommas } from 'app/libs/number';
import useSsrUserAgent from 'app/hook/common/useSsrUserAgent';

const pgPaymentURL = process.env.REACT_APP_URL_PG_PAYMENT;
const checkHost = process.env.REACT_APP_ENCAR_FEM_CHECK;
const BILL_CLOSE_USER = 'dealer';
export const PAYMENT_DATA_KEY = 'affiliatePaymentData';
const PAYMENT_STATUS_PATH = '/my/affiliate/payment/status';
// 사이트 코드: PC·모바일웹 구분은 FE만 알 수 있어 서버가 정하지 않음 (PL-14536 API)
const SITE_CODE = {
	PC: 'A0101',
	MOBILE_WEB: 'A0102',
};
// 제휴정산결제 무통장입금/카드결제 위젯 타입 (check /checkout?checkoutType= 로 전달, 토스 키·variantKey 를 PC·모바일 구분)
const CHECKOUT_TYPE = {
	PC: 'pc-card-account',
	MOBILE_WEB: 'mobile-card-account',
};
// 결제현황조회 API(PL-14536) userNotPayableReason. 버튼 노출·disabled·클릭 검증을 모두 이 코드 하나로 판정한다
const NotPayableReason = {
	BILL_NOT_ISSUED: 'BILL_NOT_ISSUED',
	CANCELED_FIRM_BILL: 'CANCELED_FIRM_BILL',
	FIRM_BILL_END: 'FIRM_BILL_END',
	NOTHING_TO_PAY: 'NOTHING_TO_PAY',
	VIRTUAL_ACCOUNT_PENDING: 'VIRTUAL_ACCOUNT_PENDING',
	ALREADY_SETTLED: 'ALREADY_SETTLED',
	PARTIALLY_PAID: 'PARTIALLY_PAID',
	UNPAID_EARLIER_BILL: 'UNPAID_EARLIER_BILL',
	PARTNERSHIP_END: 'PARTNERSHIP_END',
	PARTNERSHIP_END_TARGET: 'PARTNERSHIP_END_TARGET',
	ADJUSTMENT_IN_PROGRESS: 'ADJUSTMENT_IN_PROGRESS',
	DEALER_USAGE_NOT_CONFIRMED: 'DEALER_USAGE_NOT_CONFIRMED',
	CLOSED_BY_CONSULTANT: 'CLOSED_BY_CONSULTANT',
};
// 「결제완료(결제내역조회)」 로 표시하는 사유 (AS-IS billPayStatus === 'COMPLETE' 대치)
const PAID_REASONS = [NotPayableReason.ALREADY_SETTLED, NotPayableReason.FIRM_BILL_END, NotPayableReason.NOTHING_TO_PAY];
// 결제하기 클릭 시 딜러가 closeFirmBill 로 청구서를 발행(재발행)하고 계속 진행하는 사유 (AS-IS billStatus NONE 대치)
const REISSUE_REASONS = [NotPayableReason.BILL_NOT_ISSUED, NotPayableReason.CANCELED_FIRM_BILL];
const CONSULTANT_PAYING_MESSAGE = '컨설턴트가 결제 중입니다.\n담당 컨설턴트에게 문의해주세요.';

// 결제하기 클릭 시 사유별 안내 문구. AS-IS 문구(PC·MW 줄바꿈 포함)를 코드별로 매핑
const getNotPayableMessage = (reason, isPc) => {
	switch (reason) {
		case NotPayableReason.ADJUSTMENT_IN_PROGRESS:
			return '컨설턴트가 금액조정 중입니다.\n나중에 다시 시도해 주세요.\n자세한 내용은 담당 컨설턴트에게 문의해 주세요.';
		case NotPayableReason.DEALER_USAGE_NOT_CONFIRMED:
			return isPc
				? '금액 조정 중이거나 미확인 딜러가 있습니다.\n확인 후 결제해 주세요.'
				: '금액 조정 중이거나\n미확인 딜러가 있습니다.\n확인 후 결제해 주세요.';
		case NotPayableReason.PARTNERSHIP_END:
			return '직접 결제가 불가능합니다.\n담당 컨설턴트를 통해 결제해주세요.';
		case NotPayableReason.PARTNERSHIP_END_TARGET:
			return '컨설턴트와 상담 후 결제 가능합니다.\n담당 컨설턴트에게 문의해주세요.';
		// 아래 3종은 노출 단계에서 이미 버튼이 바뀌므로, 화면을 열어둔 사이 상태가 바뀐 경우에만 클릭 시 도달
		case NotPayableReason.VIRTUAL_ACCOUNT_PENDING:
			return '발급된 가상계좌로 입금 대기 중입니다.';
		case NotPayableReason.ALREADY_SETTLED:
			return '이미 납부가 완료된 청구서입니다.';
		case NotPayableReason.UNPAID_EARLIER_BILL:
			return '이전 달 미납 청구서를 먼저 결제해야 합니다.';
		// CLOSED_BY_CONSULTANT · CANCELED_FIRM_BILL · FIRM_BILL_END · PARTIALLY_PAID → AS-IS fallback 문구
		default:
			return CONSULTANT_PAYING_MESSAGE;
	}
};

const carTypes = {
	C: '국산',
	F: '수입',
	T: '화물',
};

// return YYYY-MM
const yearMonthToFormattedString = (year, month) => year && month && `${year}-${padStart(month, 2, '0')}`;

// 결제현황조회 API 의 yyyyMM → YYYY-MM (이미 하이픈 형식이면 그대로)
const hyphenateYearMonth = (yearMonth) =>
	yearMonth && (yearMonth.includes('-') ? yearMonth : `${yearMonth.slice(0, 4)}-${yearMonth.slice(4, 6)}`);

// 상사대표 > 가나다 순
const sortDealer = (a, b) => {
	if (a.mainUser || b.mainUser) return Number(b.mainUser) - Number(a.mainUser);
	return a.name.localeCompare(b.name);
};

// orderNo로 payNo조회
const getPayNoByOrderNo = async (orderNo) => {
	const orderResponse = await api.getPaymentOrderInfo(orderNo);
	const { payments } = orderResponse.data || {};

	return payments.length > 1 ? payments?.find((pay) => pay.paymentStatusType === 'TODAY_PAYMENT')?.no : payments?.[0].no;
};

export const ConsultantAdjustmentStatus = {
	PROGRESS: '01',
	REJECT_COMPLETE: '03',
	APPROVE_COMPLETE: '04',
};

function PaymentContainer() {
	const ua = useSsrUserAgent();
	const singleBtnSize = ua().isPc ? '100px' : undefined;
	const layerSize400 = ua().isPc ? '400px' : undefined;
	const layerSize440 = ua().isPc ? '440px' : undefined;
	const [now, setNow] = useState(null);
	const { getNow } = useNow();
	const { userInfo, checkIsChangedLoginUser } = useAuth();
	const dealerId = userInfo?.id;
	const [userSimpleInfo, setUserSimpleInfo] = useState();
	const { firmCd } = userSimpleInfo || {};
	const history = useHistory();
	const { search } = useLocation();
	const searchParams = qs.parse(search);
	const selectedYearMonth = useMemo(
		() => searchParams.year && searchParams.month && [Number(searchParams.year), Number(searchParams.month)],
		[searchParams.year, searchParams.month],
	);
	const [year, month] = selectedYearMonth || [];
	const yearMonthString = yearMonthToFormattedString(year, month);
	const [loading, setLoading] = useState(false);
	const [alertModalMessage, setAlertModalMessage] = useState('');
	const [billSummaryData, setBillSummaryData] = useState();
	const [adjustSummaryData, setAdjustSummaryData] = useState();
	const [usageByProductionData, setUsageByProductionData] = useState();
	const [usageByDealerData, setUsageByDealerData] = useState();
	const [billTargetsByCarData, setBillTargetsByCarData] = useState();
	const [consultantAdjustmentData, setConsultantAdjustmentData] = useState();
	const [quarterHistoryData, setQuarterHistoryData] = useState(null);
	const [noData, setNoData] = useState(null);
	const [noShowData, setNoShowData] = useState(null);
	const [arbitraryDecisionData, setArbitraryDecisionData] = useState(null);
	// 납부내역(settle): 결제내역조회·무통장 확인·영수증의 주문번호(trnsno) 용도로만 사용. 버튼 판정에는 쓰지 않음
	const [firmBillSettleData, setFirmBillSettleData] = useState(null);
	// 결제현황조회(PL-14536): 결제 버튼 노출·disabled·클릭 검증 전용
	const [paymentSummary, setPaymentSummary] = useState(null);
	const [billSeqForPayment, setBillSeqForPayment] = useState(null);
	const [partnershipFirmData, setPartnershipFirmData] = useState();
	const [paymentResultData, setPaymentResultData] = useState();
	const { showMaintenance, maintenanceTime, checkMaintenance, handleServiceUnavailable } = useMaintenanceCheck();
	const adjustmentData = useMemo(() => {
		const approvedData = billTargetsByCarData?.list.filter((item) => item.status === 'COMPLETE');
		const deniedData = billTargetsByCarData?.list.filter((item) => item.status === 'CANCEL');
		const totalAdjustedAmount = approvedData?.reduce((total, cur) => total + (cur.beforeAmount - cur.payamt), 0);
		const noShowapproveData = noShowData?.filter((item) => item.adjustmentStatus === 'ACCEPT');
		const noShowDeniedData = noShowData?.filter((item) => item.adjustmentStatus === 'REJECT');
		const noShowTotalAdjustedAmount = noShowapproveData?.reduce((total, cur) => total + cur.noShowAmount, 0);

		return {
			approvedCount: approvedData?.length + noShowapproveData?.length || 0,
			deniedCount: deniedData?.length + noShowDeniedData?.length || 0,
			totalAdjustedAmount: totalAdjustedAmount + noShowTotalAdjustedAmount,
		};
	}, [billTargetsByCarData, noShowData]);
	const quarterPaymentMonths = [1, 4, 7, 10];
	const dealerCountData = useMemo(
		() =>
			(usageByDealerData || []).reduce(
				(acc, cur) => {
					if (cur.status === 'COMPLETE') return { ...acc, confirmedCount: acc.confirmedCount + 1 };
					if (cur.status === 'READY') return { ...acc, adjustmentInProgressCount: acc.adjustmentInProgressCount + 1 };
					if (cur.status === 'UNCONFIRM') return { ...acc, unConfirmedCount: acc.unConfirmedCount + 1 };
					return acc;
				},
				{
					confirmedCount: 0,
					adjustmentInProgressCount: 0,
					unConfirmedCount: 0,
				},
			),
		[usageByDealerData],
	);
	const isQuarter = billSummaryData?.exactCalculationTypeCd === 'QUARTER';
	const isCarryoverMonth = isQuarter && quarterPaymentMonths.indexOf(month) < 0;
	const consultantAdjustmentStatus = consultantAdjustmentData?.aprvProgStatCd;
	const consultantAdjustmentAmount =
		consultantAdjustmentStatus === ConsultantAdjustmentStatus.APPROVE_COMPLETE ? consultantAdjustmentData.adjustmentAmount : undefined;
	// 버튼 노출·disabled 는 결제현황조회의 userNotPayableReason 으로만 판정 (PL-14536 §1-A)
	const notPayableReason = paymentSummary?.userNotPayableReason;
	const isVirtualAccountWait = notPayableReason === NotPayableReason.VIRTUAL_ACCOUNT_PENDING;
	const isPaid = PAID_REASONS.includes(notPayableReason);
	const disabledPayButton = notPayableReason === NotPayableReason.UNPAID_EARLIER_BILL;

	const initDateTime = useCallback(async () => {
		const nowDateTime = await getNow();
		setNow(nowDateTime);
	}, [getNow]);

	// 진입 시(월 미지정) 기본 달 선택·「결제기간 경과」 알림
	// - 가장 이른 미납 정산월은 결제현황조회의 earliestUnpaidYearMonth 로 판단 (AS-IS getUnPaidBill 대치)
	// - 상사 단위 값이라 조회 월은 무관하므로 현재 월로 조회. 미납 순차 차단(버튼 disabled)은 월별 판정(UNPAID_EARLIER_BILL)이 맡는다
	useEffect(() => {
		if (now && firmCd && !searchParams.error && !selectedYearMonth) {
			(async () => {
				const { data } = await api.partnership.getV2PartnershipFirmBillPaymentSummary({
					firmCode: firmCd,
					yearMonth: dayjs(now).format('YYYYMM'),
				});
				const earliestUnpaidYearMonth = hyphenateYearMonth(data?.earliestUnpaidYearMonth);
				const unPaidYearMonth = earliestUnpaidYearMonth && dayjs(`${earliestUnpaidYearMonth}-01`);
				const nextYearMonthMoment = unPaidYearMonth || dayjs(now);

				history.replace({
					search: qs.stringify({
						year: nextYearMonthMoment.year(),
						month: nextYearMonthMoment.month() + 1,
					}),
				});

				const isExpiredPayment =
					unPaidYearMonth && dayjs(`${earliestUnpaidYearMonth}-05`).endOf('date').isBefore(dayjs(now).startOf('date'));
				if (isExpiredPayment) {
					setAlertModalMessage('제휴금액 결제기간이 경과되었습니다.\n결제 진행해주세요.');
				}
			})();
		}
	}, [now, selectedYearMonth, firmCd, history, searchParams.error]);

	const initUserInfo = useCallback(async () => {
		if (!dealerId) return;

		try {
			const { data } = await api.getSimpleUserInfo(dealerId);
			const userData = data[0];

			if (searchParams.userId && dealerId !== searchParams.userId) {
				alert('올바르지 않은 접속입니다.\n로그인 페이지로 이동합니다.');
				await api.logout();
				clearAuthInfo();
				history.push(`/login?returnUrl=${history.location.pathname}${history.location.search}`);
				return;
			}

			if (userData?.affiliationType !== 'FIRM' && userData?.affiliationType !== 'SHIP') {
				alert('제휴된 상사 소속이 아닙니다.');
				return;
			}
			setUserSimpleInfo(userData);
		} catch (error) {
			loggingException(error);
			console.log(error);
			setAlertModalMessage('상사정보를 찾지 못했습니다.');
		}
	}, [dealerId, history, searchParams.userId]);

	// 결제현황조회: 진입 시 1회 + 결제하기 클릭 시 1회 (클릭 시점 최신 사유 안내 유지)
	const fetchPaymentSummary = useCallback(async () => {
		const { data } = await api.partnership.getV2PartnershipFirmBillPaymentSummary({
			firmCode: firmCd,
			yearMonth: yearMonthString.replace('-', ''),
		});
		return data;
	}, [firmCd, yearMonthString]);

	const initBillSummary = useCallback(async () => {
		if (!firmCd || !yearMonthString || !dealerId) return;
		try {
			setLoading(true);
			// 점검 여부 확인 → 점검 중이면 점검 안내 노출
			await checkMaintenance();

			const [
				billSummaryResponse,
				usageByProductionResponse,
				usageByDealerResponse,
				billTargetsByCarResponse,
				consultantAdjustmentResponse,
				hasAdjustmentSummaryResponse,
				partnershipFirmResponse,
				paymentSummaryResponse,
			] = await Promise.all([
				api.getFirmBillSummary(firmCd, yearMonthString),
				api.getFirmUsageByProduction(firmCd, yearMonthString),
				api.getFirmUsageByDealer(firmCd, yearMonthString),
				api.getFirmBillTargetsByCar(firmCd, yearMonthString),
				api.getConsultantAdjustment(firmCd, yearMonthString),
				api.getFirmBillSummary(firmCd, yearMonthString, { hasAcceptedAdjustment: true }),
				api.partnership.getPartnershipFirm(firmCd),
				fetchPaymentSummary(),
			]);

			const { billSeq } = billSummaryResponse.data || {};
			if (billSeq) {
				const billSettleResponse = await api.getBillSettle(billSeq);
				setFirmBillSettleData(billSettleResponse.data?.[0]);
			} else {
				setFirmBillSettleData(null);
			}

			if (billSummaryResponse.data?.mainuserid !== dealerId) {
				alert('권한이 없습니다.');
				setNoData(true);
				return;
			}

			if (billSummaryResponse.data.noShowCount > 0) {
				const { data } = await api.getAffiliateComplexUsageByMonth({
					yearMonth: yearMonthString,
					dealerId: '',
					noShowFlag: 'Y',
					pageableFlag: 'N',
					partnershipDivisionType: 'FIRM',
					firmCode: firmCd,
				});

				setNoShowData(data?.partnershipCalculateReadResponses);
			}

			setBillSummaryData(billSummaryResponse.data);
			setUsageByProductionData(usageByProductionResponse.data);
			usageByDealerResponse.data.sort(sortDealer);
			setUsageByDealerData(usageByDealerResponse.data);
			setBillTargetsByCarData(billTargetsByCarResponse.data);
			setConsultantAdjustmentData(consultantAdjustmentResponse.data?.firmBillAdjustment);
			setNoData(false);
			setAdjustSummaryData(hasAdjustmentSummaryResponse.data);
			setPartnershipFirmData(partnershipFirmResponse.data);
			setPaymentSummary(paymentSummaryResponse);
		} catch (error) {
			if (error.response?.data?.code === '3001') {
				setNoData(true);
				return;
			}
			loggingException(error);
			alert('죄송합니다. 데이터를 불러오지 못했습니다.');
		} finally {
			setLoading(false);
		}
	}, [firmCd, yearMonthString, dealerId, checkMaintenance, fetchPaymentSummary]);

	useEffect(() => {
		initDateTime();
	}, [initDateTime]);

	useEffect(() => {
		initUserInfo();
	}, [initUserInfo]);

	useEffect(() => {
		initBillSummary();
	}, [initBillSummary]);

	const { view: viewError, handleLayerOpen: handleLayerOpenError, handleLayerClose: handleLayerCloseError } = useLayer();
	const { view: viewBillConfirm, handleLayerOpen: handleLayerOpenBill, handleLayerClose: handleLayerCloseBill } = useLayer();

	const successPaymentResult = useCallback(
		async ({ isHistoryView, payNo }) => {
			try {
				setLoading(true);
				const paymentResultResponse = await api.getPaymentInfo(payNo);
				setPaymentResultData({ isHistoryView, ...paymentResultResponse.data });
			} catch (error) {
				loggingException(error);
				handleLayerOpenError();
			} finally {
				setLoading(false);
			}
		},
		[handleLayerOpenError],
	);

	const handlePaymentDetail = useCallback(
		async ({ isHistoryView } = {}) => {
			try {
				setLoading(true);
				const orderNo = firmBillSettleData.trnsno;
				const payNo = await getPayNoByOrderNo(orderNo);
				if (payNo) {
					successPaymentResult({ isHistoryView, payNo });
				}
			} catch (error) {
				loggingException(error);
				setLoading(false);
			}
		},
		[firmBillSettleData, successPaymentResult],
	);

	useEffect(() => {
		if (searchParams.ok) {
			successPaymentResult({
				payNo: searchParams.payNo,
				billSeq: searchParams.billSeq,
			});
		}
	}, [searchParams.ok, searchParams.payNo, searchParams.billSeq, successPaymentResult]);

	useEffect(() => {
		if (!paymentResultData && searchParams.error) {
			handleLayerOpenError();
		}
	}, [handleLayerOpenError, paymentResultData, searchParams.error]);

	const handleYearMonth = async ([selectedYear, selectedMonth]) => {
		history.replace({
			search: qs.stringify({ year: selectedYear, month: selectedMonth }),
		});
	};

	// 결제하기: 결제현황 재조회로 결제 가능 여부를 판정하고 통과 시 결제 레이어 오픈 (PL-14536 §1-A)
	// - 화면에 남는 쓰기 동작은 청구서 발행(closeFirmBill) 하나뿐. 미발행·취소면 발행 후 재조회해 그 응답으로 계속 판정
	// - 주문 생성 API 에는 제휴 종료 가드가 없어 userPayable 반영이 유일한 방어선
	const handlePayment = async () => {
		try {
			setLoading(true);
			// 정산 마감(점검) 중이면 결제 진행 차단 및 점검 안내 노출
			if (await checkMaintenance()) return;

			let summary = await fetchPaymentSummary();
			// AS-IS 의 billStatus NONE(청구서 없음 + 최신 청구서 취소) 과 동일하게 딜러가 직접 발행 후 결제
			if (REISSUE_REASONS.includes(summary.userNotPayableReason)) {
				await api.closeFirmBill(firmCd, yearMonthString, BILL_CLOSE_USER);
				summary = await fetchPaymentSummary();
			}
			// 클릭 시점 상태를 버튼 노출에도 반영 (열어둔 사이 결제완료·입금대기로 바뀐 경우)
			setPaymentSummary(summary);

			if (!summary.userPayable) {
				// 낼 금액이 없으면 안내 대신 화면 갱신 (AS-IS totalAmount === 0 동작 유지)
				if (summary.userNotPayableReason === NotPayableReason.NOTHING_TO_PAY) {
					await initBillSummary();
					return;
				}
				setAlertModalMessage(getNotPayableMessage(summary.userNotPayableReason, ua().isPc));
				return;
			}

			setBillSeqForPayment(summary.billSeq);
		} catch (error) {
			if (handleServiceUnavailable(error)) return;
			loggingException(error);
			console.error(error);
			const errorMessage = error?.response?.data?.message || '죄송합니다. 일시적으로 문제가 발생했습니다.';
			alert(errorMessage);
		} finally {
			setLoading(false);
		}
	};

	const handleQuarterHistory = async () => {
		try {
			setLoading(true);
			const currentQuarter = Math.ceil(month / 3);
			const prevQuarter = currentQuarter - 1 || 4;
			const startMonth = isCarryoverMonth ? (currentQuarter - 1) * 3 + 1 : (prevQuarter - 1) * 3 + 1;
			const endMonth = startMonth + 2;
			const quarterYear = month === 1 ? year - 1 : year;
			const startYearMonth = dayjs()
				.year(quarterYear)
				.month(startMonth - 1)
				.format('YYYY-MM');
			const endYearMonth = dayjs()
				.year(quarterYear)
				.month(endMonth - 1)
				.format('YYYY-MM');
			const yearMonths = [];
			for (let m = dayjs(startYearMonth); m.isSameOrBefore(endYearMonth); m = m.add(1, 'month')) {
				yearMonths.push(m.format('YYYY-MM'));
			}

			const { data } = await api.getQuarterExcessHistory(firmCd, yearMonthString);
			setQuarterHistoryData({ ...data, yearMonths });
		} catch (error) {
			loggingException(error);
			console.log(error);
			alert('죄송합니다. 월별내역 조회중에 문제가 발생했습니다. 잠시후 다시 시도해주세요.');
		} finally {
			setLoading(false);
		}
	};

	const submitArbitraryDecision = async () => {
		if (checkIsChangedLoginUser()) {
			alert('로그인정보가 변경되었습니다.');
			window.location.reload();
			return;
		}

		try {
			setLoading(true);
			setArbitraryDecisionData(null);
			await api.confirmPaymentAmount(firmCd, yearMonthString, arbitraryDecisionData.id);
			const { data } = await api.getFirmUsageByDealer(firmCd, yearMonthString);
			data.sort(sortDealer);
			setUsageByDealerData(data);
		} catch (error) {
			if (handleServiceUnavailable(error)) return;
			loggingException(error);
			console.log(error);
			setAlertModalMessage(error.response?.data?.message || '죄송합니다. 문제가 발생했습니다.');
		} finally {
			setLoading(false);
		}
	};

	const handlePaymentPrint = async () => {
		try {
			const orderNo = firmBillSettleData.trnsno;
			const payNo = await getPayNoByOrderNo(orderNo);
			const { data } = await api.getPaymentInfo(payNo);
			const query = {
				mertid: data.gateway.mallId,
				tid: data.gateway.transactionId,
				authdata: data.gateway.hashData,
			};
			const url = `${pgPaymentURL}/pg/wmp/etc/jsp/Receipt_Link.jsp?${qs.stringify(query)}`;
			window.open(url, '_blank', 'width=1024,height=800');
		} catch (error) {
			loggingException(error);
			setAlertModalMessage('결제내역 조회에 실패했습니다.');
		}
	};

	const handleExcelDownload = async () => {
		if (!billTargetsByCarData?.list?.length && !noShowData) {
			setAlertModalMessage('이용내역이 없습니다.');
			return;
		}

		try {
			setLoading(true);
			const XLSX = await import('xlsx');
			const headers = ['등록일', '딜러명', '차량구분', '차량번호', '차량명', '결제상품', '차량가격', '결제금액', '등록구분', '보증광고'];
			const rows = billTargetsByCarData
				? billTargetsByCarData.list.map((row) => [
						row.frstrgsdtString,
						row.name,
						carTypes[row.carType],
						row.carNo,
						`${row.manufacturerNm} ${row.modelNm}`,
						row.prodnm,
						numberWithCommas(row.carPrice),
						numberWithCommas(row.payamt),
						row.webresStat,
						row.extendWarrantyFlag ? 'Y' : 'N',
					])
				: [];

			const noShowRows = noShowData
				? noShowData.map((row) => [
						row.issueDate,
						row.dealerName,
						row.vehicleType === 'CAR_DOMESTIC' ? '국산' : '수입',
						row.vehicleNumber,
						`${row.manufacturerName} ${row.modelName}`,
						row.productName,
						0,
						numberWithCommas(row.adjustmentStatus === 'ACCEPT' ? 0 : row.noShowAmount),
						'No-Show',
					])
				: [];
			const sheet = XLSX.utils.aoa_to_sheet([headers, ...rows, ...noShowRows]);
			const book = XLSX.utils.book_new();

			const excelFileName = `${dayjs().format('YYYYMMDD_HHmmss')}.xls`;
			const sheetName = 'Sheet1';
			XLSX.utils.book_append_sheet(book, sheet, sheetName);
			XLSX.writeFile(book, excelFileName);
		} catch (error) {
			console.log(error);
			loggingException(error);
			alertModalMessage('엑셀 다운로드 중 문제가 발생했습니다.');
		} finally {
			setLoading(false);
		}
	};

	const handleExcelDownloadDetail = async () => {
		try {
			const { data } = await api.getFirmBillTargetsByCarDetail(firmCd, yearMonthString);
			if (!data.length && !noShowData) {
				setAlertModalMessage('이용내역이 없습니다.');
				return;
			}
			const XLSX = await import('xlsx');
			const headers = [
				'등록일',
				'딜러명',
				'차량구분',
				'차량번호',
				'차량명',
				'차량가격',
				'결제상품',
				'상품단가',
				'결제금액',
				'등록구분',
				'보증광고',
			];

			const rows = data
				? data.map((row) => [
						row.frstrgsdt,
						row.name,
						carTypes[row.carType],
						row.carNo,
						`${row.manufacturerNm} ${row.modelNm}`,
						numberWithCommas(row.carPrice),
						row.prodnm,
						numberWithCommas(row.prodAmt),
						numberWithCommas(row.payAmt),
						row.webresStat,
						row.extendWarrantyFlag ? 'Y' : 'N',
					])
				: [];

			const noShowRows = noShowData
				? noShowData.map((row) => [
						row.issueDate,
						row.dealerName,
						row.vehicleType === 'CAR_DOMESTIC' ? '국산' : '수입',
						row.vehicleNumber,
						`${row.manufacturerName} ${row.modelName}`,
						0,
						row.productName,
						0,
						numberWithCommas(row.adjustmentStatus === 'ACCEPT' ? 0 : row.noShowAmount),
						'No-Show',
					])
				: [];

			const sheet = XLSX.utils.aoa_to_sheet([headers, ...rows, ...noShowRows]);
			const book = XLSX.utils.book_new();
			const excelFileName = `${dayjs().format('YYYYMMDD_HHmmss')}_상세.xls`;
			XLSX.utils.book_append_sheet(book, sheet, 'Sheet1');
			XLSX.writeFile(book, excelFileName);
		} catch (error) {
			console.log(error);
			loggingException(error);
			alertModalMessage('엑셀 다운로드 중 문제가 발생했습니다.');
		} finally {
			setLoading(false);
		}
	};

	const handleBillConfirm = useCallback(async () => {
		try {
			await api.postAllBillConfirm(firmCd, yearMonthString);
			setAlertModalMessage('전결이 완료되었습니다.');
		} catch (error) {
			if (handleServiceUnavailable(error)) return;
			loggingException(error);
			setAlertModalMessage(error.response?.data?.message || '죄송합니다. 문제가 발생했습니다.');
			console.log(error);
		} finally {
			handleLayerCloseBill();
			initBillSummary();
		}
	}, [firmCd, handleLayerCloseBill, handleServiceUnavailable, initBillSummary, yearMonthString]);

	// 결제하기: 청구서 주문 생성(PL-14536) → Toss 결제위젯({CHECK}/checkout?orderNo=) 이동
	// - 결제수단(카드·무통장) 선택은 위젯이 제공하므로 이 화면에서는 받지 않음
	// - 청구서 마감·납부내역 생성은 서버가 결제완료 webhook 으로 처리
	const handlePaymentCharge = useCallback(async () => {
		const { isPc } = ua();
		// 팝업 차단 회피: 주문 생성(비동기) 전에 사용자 클릭 컨텍스트에서 빈 팝업을 먼저 연다
		const paymentPopup = isPc ? window.open('', 'affiliate_payment', 'width=800,height=700') : null;

		try {
			setLoading(true);
			const returnQuery = qs.stringify({ year, month, billSeq: billSeqForPayment });
			const returnUrl = `${window.location.origin}${PAYMENT_STATUS_PATH}?${returnQuery}`;

			sessionStorage?.setItem(PAYMENT_DATA_KEY, JSON.stringify({ billSeq: billSeqForPayment, year, month }));

			const { data } = await api.partnership.postV2PartnershipFirmBillOrder({
				billSeq: billSeqForPayment,
				// 서버 금액 규칙: 잔액(100원 단위 절사) 전액과 일치해야 함
				totalAmount: cutting10units(billSummaryData.totalAmount),
				siteCode: isPc ? SITE_CODE.PC : SITE_CODE.MOBILE_WEB,
				clientPayload: {
					// 위젯은 복귀 시 orderNo 만 붙이므로 billSeq 를 미리 박아둔다
					successUrl: returnUrl,
					failUrl: `${returnUrl}&error=true`,
					// redirectSelf 미지정: PC 팝업에서 결제 완료 시 위젯이 부모창(이 페이지)을 successUrl 로 이동시키고 팝업을 닫음
				},
			});

			const paymentUrl = `${checkHost}/checkout?${qs.stringify({
				orderNo: data.orderNo,
				checkoutType: isPc ? CHECKOUT_TYPE.PC : CHECKOUT_TYPE.MOBILE_WEB,
			})}`;
			setBillSeqForPayment(null);
			if (paymentPopup) paymentPopup.location.href = paymentUrl;
			else if (isPc) window.open(paymentUrl, '_blank', 'width=800,height=700');
			else window.location.href = paymentUrl;
		} catch (error) {
			paymentPopup?.close();
			if (handleServiceUnavailable(error)) return;
			loggingException(error);
			// 서버 오류 message 는 사용자 문구라 그대로 노출 가능 (PL-14536 API)
			setAlertModalMessage(error?.response?.data?.message || '죄송합니다. 일시적으로 문제가 발생했습니다.');
		} finally {
			setLoading(false);
		}
	}, [ua, year, month, billSeqForPayment, billSummaryData, handleServiceUnavailable]);

	if (!now || !selectedYearMonth || noData === null) return <LoadingBar />;

	return (
		<>
			<PaymentCharge
				now={now}
				noData={noData}
				firmName={userSimpleInfo?.firmNm || ''}
				selectedYearMonth={selectedYearMonth}
				billSummaryData={billSummaryData}
				dealerCountData={dealerCountData}
				consultantAdjustmentStatus={consultantAdjustmentStatus}
				consultantAdjustmentAmount={consultantAdjustmentAmount}
				isPaid={isPaid}
				isQuarter={isQuarter}
				isCarryoverMonth={isCarryoverMonth}
				isVirtualAccountWait={isVirtualAccountWait}
				disabledPayButton={disabledPayButton}
				onChangeYearMonth={handleYearMonth}
				onClickPayment={handlePayment}
				onClickHistoryMonth={handleQuarterHistory}
				onClickPaymentHistory={() => handlePaymentDetail({ isHistoryView: true })}
				onClickVirtualAccount={handlePaymentDetail}
				diagnosis2PlusUseFlag={partnershipFirmData?.diagnosis2PlusUseFlag}
			/>
			{noData === false && (
				<div style={{ overflow: 'hidden' }}>
					<div style={{ float: ua().isPc && 'left' }}>
						<PaymentServiceDetail
							yearMonth={yearMonthString}
							usageByProductionData={usageByProductionData}
							adjustSummaryData={adjustSummaryData}
						/>
						<PaymentAdjustmentDetail
							adjustmentApprovedCount={adjustmentData.approvedCount}
							adjustmentDeniedCount={adjustmentData.deniedCount}
							adjustmentAmount={adjustmentData.totalAdjustedAmount}
						/>
					</div>
					<PaymentDealerDetail
						firmCode={firmCd}
						yearMonth={yearMonthString}
						usageByDealerData={usageByDealerData}
						onClickArbitraryDecision={setArbitraryDecisionData}
						onClickExcel={handleExcelDownload}
						onClickExcelDetail={handleExcelDownloadDetail}
						onClickBillConfirm={handleLayerOpenBill}
					/>
				</div>
			)}
			{billSeqForPayment && (
				<LayerComm
					title="상사제휴 결제"
					layerClass="uilayer_normal"
					layerSize={layerSize440}
					useCancelBtn
					useSubmitBtn
					submitBtnText="결제하기"
					dimmedClick={false}
					onCancel={() => setBillSeqForPayment(null)}
					onSubmit={handlePaymentCharge}
				>
					<PaymentChargeLayer
						selectedYearMonth={selectedYearMonth}
						isQuarter={isQuarter}
						isCarryoverMonth={isCarryoverMonth}
						contractAmount={billSummaryData.contractAmount}
						extraAmount={billSummaryData.extraAmount}
						totalAmount={cutting10units(billSummaryData.totalAmount)}
						consultantAdjustmentAmount={consultantAdjustmentAmount}
					/>
				</LayerComm>
			)}
			{paymentResultData && (
				<LayerComm
					title={paymentResultData.isHistoryView ? '결제내역조회' : '상사제휴 결제'}
					useSubmitBtn
					submitBtnText="확인"
					onClose={() => setPaymentResultData(null)}
					onSubmit={() => setPaymentResultData(null)}
					layerClass="uilayer_normal"
					layerSize={layerSize440}
					btnSize={singleBtnSize}
				>
					<PaymentChargeCompleteLayer
						paymentResultData={paymentResultData}
						onClickPrint={handlePaymentPrint}
					/>
				</LayerComm>
			)}
			{viewError && (
				<LayerComm
					title={'결제에 실패하였습니다.\n나중에 다시 시도해주세요.'}
					useSubmitBtn
					onClose={handleLayerCloseError}
					onSubmit={handleLayerCloseError}
					layerSize={layerSize400}
					btnSize={singleBtnSize}
					alignCenter
				/>
			)}
			{viewBillConfirm && (
				<LayerComm
					title="일괄전결"
					useSubmitBtn
					onClose={handleLayerCloseBill}
					onCancel={handleLayerCloseBill}
					onSubmit={handleBillConfirm}
					layerSize="340px"
					useCancelBtn
				>
					담당 딜러를 대신하여 이용 금액을 일괄 전결로 확정하시겠습니까?
					<br />
					이후에는 금액조정 신청이 불가합니다.
					<br />
					(조정중인 건은 제외됩니다.)
				</LayerComm>
			)}
			{quarterHistoryData && (
				<LayerComm
					title="월별 초과금액 내역"
					useSubmitBtn
					submitBtnText="확인"
					onClose={() => setQuarterHistoryData(null)}
					onSubmit={() => setQuarterHistoryData(null)}
					layerClass="uilayer_normal"
					btnSize={singleBtnSize}
				>
					<PaymentHistoryMonthLayer
						selectedYearMonth={yearMonthString}
						quarterHistoryData={quarterHistoryData}
						isCarryoverMonth={isCarryoverMonth}
					/>
				</LayerComm>
			)}
			{alertModalMessage && (
				<LayerComm
					title={alertModalMessage}
					useSubmitBtn
					submitBtnText="확인"
					onClose={() => setAlertModalMessage('')}
					onSubmit={() => setAlertModalMessage('')}
					alignCenter
					btnSize={singleBtnSize}
				/>
			)}
			{arbitraryDecisionData && (
				<LayerComm
					title={'담당 딜러를 대신하여\n이용금액을 확정하시겠습니까?\n이후에는 금액조정 신청이 불가능합니다.'}
					useSubmitBtn
					useCancelBtn
					cancelBtnText="취소"
					submitBtnText="확인"
					onClose={() => setArbitraryDecisionData(null)}
					onCancel={() => setArbitraryDecisionData(null)}
					onSubmit={submitArbitraryDecision}
					alignCenter
					layerSize={layerSize400}
				/>
			)}
			{showMaintenance && (
				<MaintenanceNotice
					show={showMaintenance}
					maintenanceTime={maintenanceTime}
				/>
			)}
			{loading && <LoadingBar />}
		</>
	);
}

export default PaymentContainer;
